const RESTRICTED_IN_APP_BROWSER_UA = /Instagram|FBAN|FBAV|Facebook/i;
const TIKTOK_PIXEL_SRC = /analytics\.tiktok\.com|tiktok\.com\/i18n\/pixel/i;
const TIKTOK_PIXEL_INLINE = /TiktokAnalyticsObject|analytics\.tiktok\.com\/i18n\/pixel/;

type AppendNodes = (this: ParentNode, ...nodes: (Node | string)[]) => void;

let restoreTikTokPixelBlock: (() => void) | null = null;

export function isRestrictedInAppBrowser(userAgent?: string): boolean {
  const ua = userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent);
  return RESTRICTED_IN_APP_BROWSER_UA.test(ua);
}

function isTikTokPixelScript(node: Node): boolean {
  if (!(node instanceof HTMLScriptElement)) {
    return false;
  }

  const src = node.getAttribute('src') || node.src || '';
  if (TIKTOK_PIXEL_SRC.test(src)) {
    return true;
  }

  return TIKTOK_PIXEL_INLINE.test(node.textContent || '');
}

function shouldBlockTikTokPixel(node: Node | string): boolean {
  return typeof node !== 'string' && isTikTokPixelScript(node) && isRestrictedInAppBrowser();
}

function wrapAppend(original: AppendNodes): AppendNodes {
  return function (this: ParentNode, ...nodes: (Node | string)[]) {
    const allowed = nodes.filter((node) => !shouldBlockTikTokPixel(node));
    if (!allowed.length) {
      return;
    }
    original.apply(this, allowed);
  };
}

/**
 * O container de produção injeta o snippet inline do TikTok em toda página.
 * No webview do Instagram/Facebook esse snippet quebra e não pode iniciar.
 * O bloqueio entra antes do gtm.js e só descarta script do pixel.
 */
export function blockTikTokPixelInRestrictedInAppBrowser(): void {
  if (restoreTikTokPixelBlock || typeof Node === 'undefined' || !isRestrictedInAppBrowser()) {
    return;
  }

  const originalAppendChild = Node.prototype.appendChild;
  const originalInsertBefore = Node.prototype.insertBefore;
  const appendTargets: Array<{ proto: { append?: AppendNodes }; original: AppendNodes }> = [];

  Node.prototype.appendChild = function <T extends Node>(this: Node, node: T): T {
    if (shouldBlockTikTokPixel(node)) {
      return node;
    }
    return originalAppendChild.call(this, node) as T;
  };

  Node.prototype.insertBefore = function <T extends Node>(
    this: Node,
    node: T,
    child: Node | null,
  ): T {
    if (shouldBlockTikTokPixel(node)) {
      return node;
    }
    return originalInsertBefore.call(this, node, child) as T;
  };

  const patchAppend = (proto: { append?: AppendNodes } | undefined) => {
    if (!proto?.append) {
      return;
    }
    const original = proto.append;
    proto.append = wrapAppend(original);
    appendTargets.push({ proto, original });
  };

  patchAppend(Element.prototype);
  patchAppend(Document.prototype);
  patchAppend(DocumentFragment.prototype);

  restoreTikTokPixelBlock = () => {
    Node.prototype.appendChild = originalAppendChild;
    Node.prototype.insertBefore = originalInsertBefore;
    for (const target of appendTargets) {
      target.proto.append = target.original;
    }
    restoreTikTokPixelBlock = null;
  };
}

export function resetTikTokPixelBlockForTests(): void {
  restoreTikTokPixelBlock?.();
}
